import type { RfqProject, RfqSubmission, TimberRfqDrynessSpec, TimberSupplierStructureRef } from '../types';
import { normalizeQuotedStructures } from './timberQuoteStreamKey';
import {
  mergeTimberSupplierInviteForOrg,
  rfqProjectTimberSpeciesDryness,
  rfqProjectTimberSupplierSpecies,
  timberSupplierInvitesWithSpeciesDryness,
} from './timberRfqSuppliers';
import { normalizeTimberRfqDrynessSpec } from './supplierTimberExport';
import { isValidQuoteTotal } from './rfqQuoteTotal';
import { normalizeLeadTimeWeeksRange } from './rfqLeadTimeWeeks';

export type TimberQuoteSubmissionEdits = {
  species?: string;
  dryness?: TimberRfqDrynessSpec;
  quoteTotal?: number;
  leadTimeWeeksMin?: number;
  leadTimeWeeksMax?: number;
  quotedStructures?: TimberSupplierStructureRef[];
};

export type UpdateTimberQuoteSubmissionDeps = {
  updateRfqSubmission: (id: string, updates: Partial<RfqSubmission>) => Promise<void>;
  updateRfqProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
};

export async function updateTimberQuoteSubmission(
  deps: UpdateTimberQuoteSubmissionDeps,
  rfqProject: RfqProject,
  orgId: string,
  sub: RfqSubmission,
  edits: TimberQuoteSubmissionEdits,
): Promise<void> {
  if (!sub.id || !rfqProject.id) return;

  const submissionUpdates: Partial<RfqSubmission> = {};
  if (edits.quoteTotal != null) {
    if (!isValidQuoteTotal(edits.quoteTotal)) {
      throw new Error('Enter a valid quote amount.');
    }
    submissionUpdates.quoteTotal = edits.quoteTotal;
  }

  if (edits.species?.trim()) {
    submissionUpdates.quotedSpecies = edits.species.trim();
  }

  if (edits.dryness) {
    submissionUpdates.quotedDryness = normalizeTimberRfqDrynessSpec(edits.dryness);
  }

  if (edits.leadTimeWeeksMin != null || edits.leadTimeWeeksMax != null) {
    const range = normalizeLeadTimeWeeksRange(edits.leadTimeWeeksMin, edits.leadTimeWeeksMax);
    if (!range) {
      throw new Error('Lead time must be whole weeks with min less than or equal to max.');
    }
    submissionUpdates.leadTimeWeeksMin = range.min;
    submissionUpdates.leadTimeWeeksMax = range.max;
    submissionUpdates.leadTimeWeeks = undefined;
  }

  if (edits.quotedStructures !== undefined) {
    submissionUpdates.quotedStructures = edits.quotedStructures.length
      ? normalizeQuotedStructures(edits.quotedStructures)
      : undefined;
  }

  if (Object.keys(submissionUpdates).length > 0) {
    await deps.updateRfqSubmission(sub.id, submissionUpdates);
  }

  const activeSpecies = edits.species?.trim() || sub.quotedSpecies?.trim() || '';

  if (edits.species?.trim()) {
    const species = edits.species.trim();
    const existing = rfqProjectTimberSupplierSpecies(rfqProject, orgId);
    const merged = Array.from(new Set([...existing, species])).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
    if (sub.legacyImport || sub.staffManualUpload) {
      await deps.updateRfqProject(rfqProject.id, {
        timberSupplierInvites: mergeTimberSupplierInviteForOrg(
          orgId,
          merged,
          {},
          rfqProject.timberSupplierInvites,
        ),
      });
      return;
    }
    const drynessBySpecies: Record<string, TimberRfqDrynessSpec> = {};
    for (const sp of merged) {
      if (sp === species && edits.dryness) {
        drynessBySpecies[sp] = edits.dryness;
      } else {
        drynessBySpecies[sp] = rfqProjectTimberSpeciesDryness(rfqProject, orgId, sp);
      }
    }
    await deps.updateRfqProject(rfqProject.id, {
      timberSupplierInvites: mergeTimberSupplierInviteForOrg(
        orgId,
        merged,
        drynessBySpecies,
        rfqProject.timberSupplierInvites,
      ),
    });
    return;
  }

  if (edits.dryness && activeSpecies && !sub.legacyImport && !sub.staffManualUpload) {
    await deps.updateRfqProject(rfqProject.id, {
      timberSupplierInvites: timberSupplierInvitesWithSpeciesDryness(
        rfqProject.timberSupplierInvites,
        orgId,
        activeSpecies,
        edits.dryness,
      ),
    });
  }
}
